import React from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Box, Flex } from '@opendoor/bricks/core';
import { novo } from '@opendoor/bricks/theme';
import { media } from '@opendoor/bricks/theme/novo/foundations/breakpoints';
import { useObservability } from '@opendoor/observability';

import HelperText from './HelperText';

type RadioContainerProps = {
  error?: boolean;
};
export const RadioContainer = styled.div<RadioContainerProps>`
  border-radius: ${novo.radii.roundedSmooth};
  border: 1px solid ${novo.colors.warmgrey300};

  &:hover {
    border: 1px solid ${novo.colors.warmgrey800};
    outline: 1px solid ${novo.colors.warmgrey800};
  }

  input[type='radio']:checked + & {
    border: 1px solid ${novo.colors.warmgrey950};
    outline: 1px solid ${novo.colors.warmgrey950};
  }

  input[type='radio']:disabled + & {
    border: 1px solid ${novo.colors.warmgrey300};
    color: ${novo.colors.warmgrey700};
  }

  input[type='radio']:focus-within + & {
    border: 1px solid ${novo.colors.brandBlue600};
    outline: 2px solid ${novo.colors.brandBlue600};
  }

  ${({ error }) =>
    error
      ? css`
          border: 1px solid ${novo.colors.error500};
          outline: 1px solid ${novo.colors.error500};
          color: ${novo.colors.error500};
          padding: 1px;
        `
      : ''};
`;

export const RadioLabel = styled.label`
  padding: 18px;
  font-size: ${novo.fontSizes['16px']};
  line-height: ${novo.lineHeights['130']};
  letter-spacing: ${novo.letterSpacings['-1.4']};

  ${media.largerThan.XS} {
    padding: 21px 18px;
    font-size: ${novo.fontSizes['18px']};
    line-height: ${novo.lineHeights['120']};
    letter-spacing: ${novo.letterSpacings['-2.4']};
  }
  ${media.largerThan.SM} {
    padding: 24px 22px;
    font-size: ${novo.fontSizes['20px']};
    line-height: ${novo.lineHeights['120']};
    letter-spacing: ${novo.letterSpacings['-2.4']};
  }
  ${media.largerThan.MD} {
    padding: 28px 22px;
    font-size: ${novo.fontSizes['22px']};
    line-height: ${novo.lineHeights['112']};
    letter-spacing: ${novo.letterSpacings['-2.4']};
  }

  display: block;
  cursor: pointer;

  font-family: ${novo.fonts.sans};
  font-weight: ${novo.fontWeights.normal};
`;

export const Input = styled.input`
  opacity: 0;
  position: absolute;
`;

export type RadioInputProps = {
  id: string;
  children: React.ReactNode;
  additionalChildren?: React.ReactNode;
  errorText?: React.ReactNode;
  error?: boolean;
  disabled?: boolean;
  analyticsName?: string;
} & React.InputHTMLAttributes<HTMLInputElement>;

const RadioInput: React.FC<RadioInputProps> = ({
  id,
  disabled,
  children,
  additionalChildren,
  error,
  errorText,
  analyticsName,
  ...inputProps
}) => {
  const { trackJourneyEvent } = useObservability();

  const onClick = (e: React.MouseEvent<HTMLInputElement>) => {
    inputProps.onClick && inputProps.onClick(e);
    if (analyticsName) {
      const data = { targetName: analyticsName, optionName: inputProps.value };
      trackJourneyEvent('platform', 'radioClickEvent', data);
    }
  };

  return (
    <div>
      <Input type="radio" id={id} {...inputProps} disabled={disabled} onClick={onClick} />
      <RadioContainer error={error}>
        <RadioLabel htmlFor={id}>{children}</RadioLabel>
        {additionalChildren ? (
          <RadioLabel as="div" style={{ paddingTop: '0' }}>
            {additionalChildren}
          </RadioLabel>
        ) : null}
      </RadioContainer>
      {error && errorText && (
        <Box mt={['10px', null, '12px']}>
          <HelperText error>{errorText}</HelperText>
        </Box>
      )}
    </div>
  );
};

type RadioGroupContainerProps = {
  id?: string;
  children: React.ReactNode;
  ariaLabelledBy?: string;
};

const RadioGroupContainer: React.FC<RadioGroupContainerProps> = ({
  id,
  children,
  ariaLabelledBy,
}) => {
  return (
    <Flex
      flexDirection="column"
      gap="16px"
      id={id}
      role="radiogroup"
      aria-labelledby={ariaLabelledBy}
    >
      {children}
    </Flex>
  );
};

export type RadioGroupInputProps = {
  id: string;
  error?: boolean;
  name: string;
  analyticsName?: string;
  options: Array<RadioInputProps>;
  labelId?: string;
};

export const RadioGroupInput: React.FC<RadioGroupInputProps> = ({
  id,
  options,
  labelId,
  analyticsName,
}) => {
  return (
    <RadioGroupContainer id={id} ariaLabelledBy={labelId}>
      {options.map((props) => {
        return <RadioInput key={props.id} {...props} analyticsName={analyticsName} />;
      })}
    </RadioGroupContainer>
  );
};
